/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Wed Aug 14 2024
 **/

import { atom, useAtomValue } from "jotai";
import { atomFamily, useAtomCallback } from "jotai/utils";
import { useCallback } from "react";

import { JotaiStore } from "@/frameworks/libraries/commons";

export interface UserInfo {
	id: string;
	name: string;
	profileImagePath: string;
	colorProfile: string;
}

export interface SpaceInfo {
	id: string;
	name: string;
	thumbnailPath: string;
}

interface KnockAtom {
	UserInfo: UserInfo;
	SpaceInfo?: SpaceInfo;
	startCoordinate: { x: number; y: number };
	zIndex: number;
	focus?: boolean;
	uniqueIndex: string;
}

const knockListAtom = atom<KnockAtom[]>([]);

const knockListAtomFamily = atomFamily(
	({ uniqueIndex }: { uniqueIndex: string }) => {
		return atom((get) => {
			const knockList = get(knockListAtom);
			return knockList.find((item) => item.uniqueIndex === uniqueIndex);
		});
	},
	(prev, next) => {
		return prev.uniqueIndex === next.uniqueIndex;
	}
);

export default function useKnockManager() {
	const getAll = useAtomCallback(
		useCallback((get) => {
			return get(knockListAtom);
		}, []),
		{ store: JotaiStore }
	);

	const get = useAtomCallback(
		useCallback((get, _, { uniqueIndex }: { uniqueIndex: string }) => {
			return get(knockListAtom).find((v) => {
				return v.uniqueIndex === uniqueIndex;
			});
		}, []),
		{ store: JotaiStore }
	);

	const getLargestZIndex = useAtomCallback(
		useCallback((get) => {
			const zIndexList = get(knockListAtom).map((v) => v.zIndex);
			const largestZIndex = Math.max(...zIndexList);
			return largestZIndex;
		}, []),
		{ store: JotaiStore }
	);

	const show = useAtomCallback(
		useCallback((_, set, knockData: KnockAtom) => {
			set(knockListAtom, (prev) => {
				const newItem = { ...knockData, focus: prev.length === 0 };
				const largestZIndex = getLargestZIndex();

				const updatedList =
					prev.length > 0
						? prev.map((item) => ({
								...item,
								focus: item.zIndex === largestZIndex
							}))
						: [];

				updatedList.push(newItem);
				return updatedList;
			});
		}, []),
		{ store: JotaiStore }
	);

	const hide = useAtomCallback(
		useCallback((_, set, uniqueIndex: string) => {
			set(knockListAtom, (prev) => {
				const largestZIndex = getLargestZIndex();

				const result = [...prev].filter((item) => item.uniqueIndex !== uniqueIndex);

				return result.map((item) => {
					return {
						...item,
						zIndex: item.zIndex + 1,
						focus: item.zIndex === largestZIndex - 1 ? true : false
					};
				});
			});
		}, []),
		{ store: JotaiStore }
	);

	const getCurrentFocus = useAtomCallback(
		useCallback((get) => {
			const largestZIndex = getLargestZIndex();
			const knockList = get(knockListAtom);
			return knockList.find((v) => v.zIndex === largestZIndex);
		}, []),
		{ store: JotaiStore }
	);

	const focus = useAtomCallback(
		useCallback((_, set, uniqueIndex: string) => {
			if (getCurrentFocus()?.uniqueIndex === uniqueIndex) {
				return;
			}
			set(knockListAtom, (prev) => {
				const target = prev.find((v) => v.uniqueIndex === uniqueIndex);
				if (!target) return prev;
				const largestZIndex = getLargestZIndex();
				return prev.map((v) => {
					if (v.uniqueIndex === uniqueIndex) return { ...v, zIndex: largestZIndex, focus: true };
					else if (v.zIndex >= target.zIndex) return { ...v, zIndex: v.zIndex - 1, focus: false };
					return { ...v, focus: false };
				});
			});
		}, []),
		{ store: JotaiStore }
	);

	const clear = useAtomCallback(
		useCallback((_, set) => {
			set(knockListAtom, []);
		}, []),
		{ store: JotaiStore }
	);

	const knockActions = {
		getAll,
		get,
		show,
		hide,
		clear,
		focus,
		getCurrentFocus
	};

	const knockListValue = () => {
		return useAtomValue(knockListAtom, { store: JotaiStore });
	};

	const knockValue = (uniqueIndex: string) => {
		return useAtomValue(knockListAtomFamily({ uniqueIndex }), { store: JotaiStore });
	};

	return {
		knockActions,
		knockListValue,
		knockValue
	};
}
